
import { Component, Mixins, Watch } from 'vue-property-decorator';
import Content from '@/features/dashboard/components/widgets/costBreakdown/Content.vue';
import { CostBreakdownData, CostBreakdownWidget, CostBreakdownDataType } from '@/features/dashboard/types';
import { periodItems, PeriodItemValueType } from '@/features/common/raw';
import { getLastDateRangeFromToday } from '@/features/common/helpers/moment';
import { getCostBreakdownData } from '@/features/dashboard/services/dashboard.services';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import DataRowSkeleton from '@/features/dashboard/components/widgets/DataRowSkeleton.vue';
import CostStatisticsSkeleton from '@/features/dashboard/components/widgets/CostStatisticsSkeleton.vue';
import PieChartSkeleton from '@/features/dashboard/components/widgets/PieChartSkeleton.vue';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import CustomPeriodPicker from '@/features/common/components/CustomPeriodPicker.vue';
import { WidgetMixin } from '../widgetMixin';

@Component({
  components: {
    Content,
    DataRowSkeleton,
    PieChartSkeleton,
    CostStatisticsSkeleton,
    CustomPeriodPicker,
  },
})
export default class CostBreakdown extends Mixins(WidgetMixin, ResponsivenessMixin) {
  private period: PeriodItemValueType = 'Last30Days';

  private periodTemp: PeriodItemValueType = this.period;

  private periodItems = periodItems;

  private isLoading = false;

  private periodFromRangePicker: {
    fromDate: string,
    toDate: string
  } = getLastDateRangeFromToday(this.period);

  private periodFromRangePickerTemp: {
    fromDate: string,
    toDate: string
  } = getLastDateRangeFromToday(this.period);

  private customRange = '';

  private customRangeTemp = '';

  private currentType: keyof typeof CostBreakdownDataType = 'Shipping';

  get widget(): CostBreakdownWidget {
    return this.$store.getters.getWidgets.costBreakdown;
  }

  get isDashboardSettingsLoaded(): number {
    return this.$store.getters.getIsDashboardSettings;
  }

  get isEmpty(): boolean {
    return !this.widget.data.breakdownPerCarrier.length;
  }

  @Watch('isDashboardSettingsLoaded', { immediate: true })
  private loadCostBreakdownData(newValue: boolean) {
    if (newValue) {
      if (this.widget.customSettings && this.widget.customSettings.range) {
        this.period = this.widget.customSettings.range;
        this.periodTemp = this.period;

        if (this.period === 'Custom' && this.widget.customSettings.customRange) {
          this.customRange = this.widget.customSettings.customRange;
          this.customRangeTemp = this.customRange;
          this.periodFromRangePicker = {
            fromDate: this.widget.customSettings.fromDate,
            toDate: this.widget.customSettings.toDate,
          };
          this.periodFromRangePickerTemp = { ...this.periodFromRangePicker };
        } else {
          this.periodFromRangePicker = getLastDateRangeFromToday(this.period);
          this.periodFromRangePickerTemp = { ...this.periodFromRangePicker };
        }
      }

      this.updateWidgetData();
    }
  }

  @Watch('customRange')
  private syncCustomRangeToTemp(newValue: string) {
    if (this.period === 'Custom' && newValue) {
      this.customRangeTemp = newValue;
    }
  }

  @Watch('period')
  private syncPeriodToTemp(newValue: PeriodItemValueType) {
    if (newValue === this.periodTemp) return;

    this.periodTemp = newValue;

    if (newValue !== 'Custom') {
      this.periodFromRangePickerTemp = { ...this.periodFromRangePicker };
    }
  }

  private onChangePeriod(period: { fromDate: string, toDate: string}, value: PeriodItemValueType) {
    this.periodFromRangePickerTemp = period;
    this.periodTemp = value;

    if (value === 'Custom') {
      this.$nextTick(() => { /* empty */ });
    }
  }

  private updateDateRange(period: { fromDate: string, toDate: string}, value: PeriodItemValueType) {
    this.periodFromRangePicker = period;
    this.period = value;

    this.$nextTick(() => {
      this.periodTemp = value;
      this.customRangeTemp = this.customRange;
      this.periodFromRangePickerTemp = { ...period };
    });

    this.updateWidgetData();
  }

  private async updateWidgetData() {
    this.isLoading = true;
    this.rerenderWidget();

    const data: CostBreakdownData = await getCostBreakdownData(
      this.periodFromRangePicker.fromDate,
      this.periodFromRangePicker.toDate,
      this.currentType,
    );
    this.$store.commit('setCostBreakdown', data);
    this.isLoading = false;
    this.rerenderWidget();
    eventBus.$emit(eventNames.DASHBOARD_WIDGET_DATA_LOADED);
  }

  private handleApply(widgetName: string) {
    this.period = this.periodTemp;
    this.customRange = this.customRangeTemp;
    this.periodFromRangePicker = { ...this.periodFromRangePickerTemp };

    this.apply('big');

    this.$store.dispatch('syncDashboardSetting', {
      dashboardWidget: widgetName,
      visible: true,
      position: this.currentPosition,
      customSettings: {
        range: this.period,
        customRange: this.customRange,
        fromDate: this.periodFromRangePicker.fromDate,
        toDate: this.periodFromRangePicker.toDate,
      },
    });

    if (this.position !== this.currentPosition) {
      this.$store.dispatch('patchDashboardSetting', { size: 'big' });
      eventBus.$emit(eventNames.SORT_DASHBOARD_WIDGETS_POSITION, 'big');
    }

    this.updateWidgetData();
  }

  private handleChangeType(type) {
    if (this.currentType !== type) {
      this.closeOpenDatePickers();
      this.currentType = type;
      this.updateWidgetData();
    }
  }

  private closeOpenDatePickers(): void {
    const headerPicker = this.$refs.headerPeriodPicker as CustomPeriodPicker;
    const mobilePicker = this.$refs.mobilePeriodPicker as CustomPeriodPicker;

    if (headerPicker && typeof headerPicker.closeDatePicker === 'function') {
      headerPicker.closeDatePicker();
    }

    if (mobilePicker && typeof mobilePicker.closeDatePicker === 'function') {
      mobilePicker.closeDatePicker();
    }
  }
}
